/** @jsxImportSource @emotion/react */
"use client";

import type { Interpolation } from "@emotion/react";
import { HallowABFlag, useMixpanel } from "@packages/ab";
import { HallowAnalyticsEvent, useAnalytics } from "@packages/analytics";
import { useSearch } from "@packages/framework";
import { URL_WEB, useSession } from "@packages/sdk";
import type { ReactNode } from "react";
import { useEffect, useState } from "react";

import { useSearchParamState } from "../../../hooks/useSearchParamState";
import { Loader } from "../other/Loader/Loader.emotion";
import { AuthDOBFlow } from "./AuthDOBFlow.emotion";
import type { AuthMainFlowProps } from "./AuthMainFlow.emotion";
import { AuthMainFlow } from "./AuthMainFlow.emotion";
import { AuthNamesFlow } from "./AuthNamesFlow.emotion";
import { AuthSmsPromptFlow } from "./AuthSmsPromptFlow";
import type {
  AuthFlow,
  AuthFlowProps,
  AuthMethod,
  AuthProps,
} from "./AuthTypes.emotion";
import { EmailAuthFlow } from "./EmailAuthFlow.emotion";
import { OtpAuthFlow } from "./OtpAuthFlow.emotion";
import { PasswordAuthFlow } from "./PasswordAuthFlow.emotion";
import { PasswordAuthFlowWithSignup } from "./PasswordAuthFlowWithSignup.emotion";
import { PhoneAuthFlow } from "./PhoneAuthFlow.emotion";

type AuthComponentProps = AuthProps & {
  layoutCSS?: Interpolation;
  onFlowChange?: (flow: AuthFlow) => void;
};

export const Auth = ({
  authMainFlowProps = {},
  allowedMethods = ["email", "phone", "apple", "google"],
  hideHeader,
  onAuth,
  onError,
  onFlowChange: handleFlowChange,
  ...props
}: AuthComponentProps & { authMainFlowProps?: Partial<AuthMainFlowProps> }) => {
  const initialFlow = props.email ? "email" : props.phone ? "phone" : "main";

  const { track } = useAnalytics();
  const { flags } = useMixpanel();
  const [flow, setFlow] = useSearchParamState<false, AuthFlow>({
    defaultValue: initialFlow,
    key: "flow",
    removeFromUrlIfDefaultValue: false,
    multiple: false,
  });
  const [state, setState] = useState<Record<string, any>>({});
  const [initialCheck, setInitialCheck] = useState<boolean>(false);
  const { status } = useSession();
  const search = useSearch();
  const [method, setMethod] = useState<AuthMethod>("email");
  const [isLoading, setIsLoading] = useState(false);

  const isTargetedSmsPrompt =
    flags?.[HallowABFlag.WebOnboardingSmsPrompt]?.isTargeted;

  const { login } = useSession({
    onAuthenticated: ({ isNewUser }) => {
      setIsLoading(true);
      if (isNewUser && state?.phone && isTargetedSmsPrompt)
        setFlow("sms_prompt");
      else {
        track({
          event: HallowAnalyticsEvent.SkippedOnboardingStep,
          properties: {
            screen_name: "sms_outside_hours_prompt",
          },
        });
        if (flow !== "names" && isNewUser) setFlow("names");
        else onAuth({ isNewUser });
      }
      setIsLoading(false);
    },
  });

  useEffect(() => {
    if (
      !initialCheck &&
      !search.has("isAuthenticated") &&
      !search.has("isNewUser")
    ) {
      if (status === "authenticated") onAuth?.({ isNewUser: false });
      if (status !== "loading") setInitialCheck(true);
    }
  }, [status, initialCheck]);

  useEffect(() => {
    const urlFlow = search.get("flow");
    if (
      ["email", "phone", "phone_code", "password", "main"].includes(urlFlow)
    ) {
      setFlow(urlFlow as AuthFlow);
      handleFlowChange?.(urlFlow as AuthFlow);
    }
  }, [search]);

  /** setSessionListenerURLParams onAuth for forms  */
  const handleAuth: AuthFlowProps["onAuth"] = ({ data }) => {
    login({ isNewUser: data?.is_new, token: data?.oauth?.access_token });
  };

  const onFlowChange = (flow: AuthFlow) => {
    setFlow(flow);
    handleFlowChange?.(flow);
  };

  const flowProps = {
    ...props,
    onAuth: handleAuth,
    hideHeader,
    allowedMethods,
    flow,
    setFlow: onFlowChange,
    state,
    setState,
    method,
    setMethod,
    onError: (err) => {
      onError?.(err);
      if (err?.code === 1030) {
        // age restriction
        location.href = URL_WEB;
      }
    },
  };

  const flows: Record<AuthFlow, ReactNode> = {
    main: <AuthMainFlow key="main" {...flowProps} {...authMainFlowProps} />,
    email: <EmailAuthFlow key="email" {...flowProps} />,
    password: <PasswordAuthFlow key="password" {...flowProps} />,
    /** Allow Password signups IF NOT in current A/B magic-link treatment */
    passwordWithSignup: (
      <PasswordAuthFlowWithSignup key="passwordWithSignup" {...flowProps} />
    ),
    phone: <PhoneAuthFlow key="phone" {...flowProps} />,
    otp: <OtpAuthFlow key="otp" {...flowProps} />,
    /** Use onAuth for "names" or "dob" as needed */
    names: <AuthNamesFlow key="names" {...{ ...flowProps, onAuth }} />,
    dob: <AuthDOBFlow key="dob" {...{ ...flowProps, onAuth }} />,
    sms_prompt: (
      <AuthSmsPromptFlow key="sms_prompt" {...{ ...flowProps, onAuth }} />
    ),
  };

  if (isLoading) return <Loader />;

  return flows[flow];
};

Auth.displayName = "Auth";
